import { useCallback, useEffect, useState } from 'react'

import { useServices } from '../services/ServicesProvider.js'
import {
  readStorageStatus,
  requestPersistence,
  type StorageStatus,
} from './persistence.js'

/** Set once persistence has been asked for, so it is not asked on every launch. */
export const PERSISTENCE_ASKED_KEY = 'storage.persistenceRequested'

/**
 * The current durability state, re-read on every launch.
 *
 * Re-reading matters: installing to the Home Screen can turn a `best-effort`
 * origin into a `persistent` one, and a value cached at first run would still
 * be telling the user their data is at risk long after it stopped being true.
 */
export function useStorageStatus(): {
  readonly status: StorageStatus | null
  readonly refresh: () => void
} {
  const { storage } = useServices()
  const [status, setStatus] = useState<StorageStatus | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function evaluate() {
      /**
       * Ask once, and only after something has been stored.
       *
       * Requested with nothing saved, browsers are at their most likely to
       * refuse — there is no engagement signal to go on — and a refusal cannot
       * be retried. Spending the one attempt on the worst possible moment is
       * how an app ends up permanently `best-effort` for no reason.
       */
      const [asked, entries] = await Promise.all([
        storage.settings.get(PERSISTENCE_ASKED_KEY),
        storage.entries.count(),
      ])

      if (asked !== 'true' && entries > 0) {
        await requestPersistence()
        await storage.settings.set(PERSISTENCE_ASKED_KEY, 'true')
      }

      const current = await readStorageStatus()
      if (!cancelled) {
        setStatus(current)
      }
    }

    void evaluate()
    return () => {
      cancelled = true
    }
  }, [storage, nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  return { status, refresh }
}
